import CookiesJS from 'cookies-js';
import { notify } from '../../notifications';
import { getRebuildWidgetState } from '../../../lib/rebuildWidget';

/**
 * Виджет «Обновить программу» в карточке клиента.
 *
 * Сборка занимает около минуты и меняет человеку всю неделю, поэтому
 * кнопка одноразовая: после нажатия гаснет и ждёт, пока сборка
 * закончится. О завершении сообщаем нотификацией — иначе непонятно,
 * получилось или нет.
 *
 * Состояние считает lib/rebuildWidget, тот же модуль рисует виджет
 * на сервере. До этого после перезагрузки страницы человек видел
 * «параметры изменены» и активную кнопку, даже когда сборка шла или
 * уже провалилась (тест 07.10).
 */
const POLL_EVERY_MS = 10000;
const POLL_LIMIT_MS = 5 * 60 * 1000;

function widget() {
    return document.querySelector('.programm-widget');
}

// Сколько держим на экране удачный исход, прежде чем убрать виджет,
// появившийся по сохранению. Две секунды: успеть прочитать, но
// не оставлять на экране то, что уже не требует внимания.
const SUCCESS_LINGER_MS = 2000;

// Пауза перед перезагрузкой после удачной сборки: успеть увидеть
// «Программа создана», прежде чем страница обновится.
const RELOAD_AFTER_BUILD_MS = 1200;

function applyState(block, status) {
    const state = getRebuildWidgetState(status, {
        viewerIsOwner: block.getAttribute('data-foreign') !== '1',
    });
    const title = block.querySelector('.programm-widget__title');
    const note = block.querySelector('.programm-widget__note');
    const button = block.querySelector('.action__rebuild-programms');

    if (title) title.textContent = state.title;
    if (note) note.textContent = state.note;

    // Класс состояния один, поэтому старый снимаем целиком: иначе
    // «создаётся» и «не создалась» накладываются друг на друга.
    block.className = block.className
        .split(' ')
        .filter(name => name.indexOf('programm-widget--') !== 0 || name === 'programm-widget--compact')
        .join(' ');
    block.classList.add(`programm-widget--${state.state}`);
    block.setAttribute('data-widget-state', state.state);
    // Виджет на формах показывается только после сохранения: при
    // загрузке страницы ему нечего сказать. Признак ставит runBuild.
    if (block.getAttribute('data-transient') !== '1' || block.getAttribute('data-revealed') === '1') {
        block.hidden = false;
    }

    if (button) {
        // Кнопка со словами: и сервер, и браузер пишут в неё текст.
        // Прежде сервер рисовал значок, а браузер писал текст поверх —
        // значок исчезал, а надпись вываливалась из круглой кнопки
        // (приёмка 17.10). Потом значок остался один и оказался
        // непонятен (приёмка 18.10), поэтому вернулись к словам.
        button.hidden = !state.showButton;
        button.disabled = !state.showButton;
        button.textContent = state.buttonText;
        // Оплата — не кнопка, а ссылка, и её рисует сервер. Если
        // состояние сменилось на «не оплачено» уже в браузере,
        // кнопку прячем: запускать сборку всё равно нельзя.
        if (state.payment) button.hidden = true;
    }

    // Виджет, появившийся по сохранению, при удаче сам уходит: держать
    // на экране «всё хорошо» незачем. Постоянный — на главной и в фокусе —
    // остаётся: там он и есть ответ на вопрос о программе.
    if (state.success && block.getAttribute('data-transient') === '1') {
        setTimeout(() => {
            if (block.getAttribute('data-widget-state') === 'ready') block.hidden = true;
        }, SUCCESS_LINGER_MS);
    }

    return state;
}

// Состояние виджета считает сервер: браузер не знает, когда программу
// собирали в прошлый раз.
export async function refreshRebuildWidget(apiClient) {
    const block = widget();
    if (!block) return undefined;
    const clientId = block.getAttribute('data-client-id');
    try {
        const status = await apiClient.getProgrammsStatus(clientId, CookiesJS.get('auth_token'));
        applyState(block, status);
        return status;
    } catch (error) {
        console.error(error);
        return undefined;
    }
}

// Сборка и наблюдение за ней. Нужны в двух местах: по кнопке
// «Попробовать снова» и сразу после сохранения параметров или
// исключений — в режиме автогенерации сохранение и есть запуск.
async function runBuild(apiClient, { silentReject = false } = {}) {
    const block = widget();
    if (!block) return;
    const startedAt = Date.now();
    block.setAttribute('data-revealed', '1');
    applyState(block, { buildState: 'running' });
    try {
        await apiClient.rebuildProgramms(
            block.getAttribute('data-client-id'),
            CookiesJS.get('auth_token'),
        );
    } catch (error) {
        const message = error
            && error.response
            && error.response.data
            && (error.response.data.errors || [])[0];
        // После сохранения отказ — обычное дело: сборка уже могла
        // идти, или менять было нечего. Шуметь об этом не нужно,
        // достаточно показать настоящее состояние.
        if (!silentReject) {
            notify(
                (message && message.formattedMessage) || 'Не удалось запустить сборку программы',
                'error',
            );
        }
        await refreshRebuildWidget(apiClient);
        return;
    }
    watchBuild(apiClient, startedAt, false);
}

// Ждём, пока сборка закончится. Признак успеха — у расписания
// обновилась дата последней сборки; признак неудачи — задача
// закрылась ошибкой. Раньше неудачу виджет не замечал вовсе
// и крутился все пять минут до таймаута.
function watchBuild(apiClient, startedAt, sawRunning) {
    let seenRunning = sawRunning;
    let attempts = 0;
    const step = async () => {
        if (Date.now() - startedAt > POLL_LIMIT_MS) {
            await refreshRebuildWidget(apiClient);
            notify('Сборка идёт дольше обычного. Обновите страницу через пару минут.', 'warning');
            return;
        }
        attempts += 1;
        const status = await refreshRebuildWidget(apiClient);
        if (status && status.buildState === 'running') seenRunning = true;
        const done = status
            && status.lastProgrammsUpdate
            && new Date(status.lastProgrammsUpdate).getTime() > startedAt;
        if (done) {
            // Виджет сказал «готово», а страница всё ещё показывает
            // старое: меню, список покупок и баланс сервер отдал при
            // загрузке. Человек обновлял вручную, и не по одному разу
            // (QA 17.10). Перезагружаем сами — но только когда в статусе
            // уже есть программы, иначе покажем пустой экран вместо неё.
            const page = widget();
            if (status.hasProgramms && page && page.getAttribute('data-transient') !== '1') {
                setTimeout(() => window.location.reload(), RELOAD_AFTER_BUILD_MS);
            }
            return;
        }
        // Задача закрылась ошибкой. Ждём либо увиденного «идёт»,
        // либо двух опросов: сразу после запуска статус ещё может
        // показывать исход прошлой сборки.
        if (status && status.buildState === 'failed' && (seenRunning || attempts >= 2)) return;
        setTimeout(step, POLL_EVERY_MS);
    };
    setTimeout(step, POLL_EVERY_MS);
}

// Сохранение в режиме автогенерации запускает сборку. Виджет
// показывается сразу, не дожидаясь ответа сервера: иначе кажется,
// что нажатие ничего не сделало.
export function startBuildAfterSave(apiClient) {
    return runBuild(apiClient, { silentReject: true });
}

export function initRebuildProgramms($, apiClient) {
    const block = widget();
    if (!block) return;
    const button = block.querySelector('.action__rebuild-programms');
    if (!button) return;

    // На формах виджет молчит до сохранения — опрашивать нечего.
    if (block.getAttribute('data-transient') === '1') return;

    // На постоянных страницах всегда сверяемся с сервером: их могли
    // открыть во время сборки или после неудачной.
    refreshRebuildWidget(apiClient).then(status => {
        if (status && status.buildState === 'running') {
            // Сборку запустили раньше — досматриваем её до конца.
            watchBuild(apiClient, Date.now() - POLL_EVERY_MS, true);
        }
    });

    button.addEventListener('click', () => {
        if (button.disabled) return;
        return runBuild(apiClient);
    });
}
